// Fan portal "Upcoming & Live battles" section. Shows scheduled + live
// battles to spectators with both silo names AND creator names so the
// audience knows WHO's battling. Per Randy's original spec.
//
// Auto-hides when the master toggle is off OR when there's nothing
// running.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sword, Hourglass, Play, ArrowRight } from "lucide-react";
import { listBattles } from "./api";
import { useBattlesEnabled } from "./useBattlesEnabled";

export default function FanPortalBattlesSection() {
  const { enabled } = useBattlesEnabled();
  const [live, setLive] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled) return;
    let cancel = false;
    const load = async () => {
      try {
        const [l, u] = await Promise.all([listBattles("live"), listBattles("upcoming")]);
        if (cancel) return;
        setLive(l || []);
        // Filter to actually scheduled (skip pending_acceptance — those aren't watch-ready)
        setUpcoming((u || []).filter(b => ["scheduled", "ready"].includes(b.state) && b.is_broadcast !== false));
      } catch { /* ignore */ }
      finally { if (!cancel) setLoading(false); }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancel = true; clearInterval(t); };
  }, [enabled]);

  if (!enabled || loading) return null;
  if (live.length === 0 && upcoming.length === 0) return null;

  return (
    <section className="mt-12" data-testid="fan-portal-battles-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif-display text-2xl text-[#1C1B1A] flex items-center gap-2">
          <Sword size={20} className="text-[#C16E5A]" />
          Silo Wars
        </h2>
        <Link to="/wars" className="text-xs text-[#6B6A68] hover:text-[#1C1B1A] flex items-center gap-1" data-testid="fan-portal-battles-all">
          See all <ArrowRight size={11} />
        </Link>
      </div>

      {live.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#C16E5A] mb-2 flex items-center gap-1">
            <Play size={11} /> Live now
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            {live.slice(0, 4).map(b => <BattleStrip key={b.id} battle={b} accent="#C16E5A" />)}
          </div>
        </>
      )}

      {upcoming.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#A89360] mb-2 flex items-center gap-1">
            <Hourglass size={11} /> Coming up
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {upcoming.slice(0, 6).map(b => <BattleStrip key={b.id} battle={b} accent="#A89360" />)}
          </div>
        </>
      )}
    </section>
  );
}

function BattleStrip({ battle, accent }) {
  const l = battle.challenger_silo;
  const r = battle.target_silo;
  return (
    <Link
      to={`/wars/arena/${battle.id}`}
      className="block bg-white border border-[#D8D6D2] rounded-lg p-4 hover:shadow-md transition"
      style={{ borderLeftColor: accent, borderLeftWidth: 3 }}
      data-testid={`fan-portal-battle-${battle.id}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: accent }}>
          {battle.state === "live" ? "● LIVE" : (battle.scheduled_for ? new Date(battle.scheduled_for).toLocaleString() : "Soon")}
        </span>
        {battle.topic && <span className="text-[10px] text-[#6B6A68] truncate ml-2">on "{battle.topic}"</span>}
      </div>
      <div className="text-sm text-[#1C1B1A] mb-1">
        <strong>{l?.site_name}</strong>
        <span className="mx-1.5 text-[10px] uppercase tracking-widest text-[#A89360]">vs</span>
        <strong>{r?.site_name || "TBD"}</strong>
      </div>
      <div className="text-[11px] text-[#6B6A68]">
        {l?.owner_name && <span>by {l.owner_name}</span>}
        {l?.owner_name && r?.owner_name && <span> · </span>}
        {r?.owner_name && <span>vs {r.owner_name}</span>}
      </div>
    </Link>
  );
}
